import { useRef, useState } from "react";
import { Paperclip, X, FileText as FileIcon, Image as ImageIcon, Loader2, Download, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "../lib/api";
import { Button } from "./ui/button";

const ICON = (ct) => (ct?.startsWith("image/") ? ImageIcon : FileIcon);

export function FileUploader({ files, setFiles, purpose = "attachment", testid }) {
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const ref = useRef(null);

  const uploadFiles = async (fileList) => {
    if (!fileList.length) return;
    setBusy(true);
    const uploaded = [];
    for (const f of fileList) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post(`/files/upload?purpose=${purpose}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push({ id: data.id, filename: data.original_filename, content_type: data.content_type, size: data.size });
      } catch (err) { toast.error(formatError(err)); }
    }
    setFiles([...(files || []), ...uploaded]);
    setBusy(false);
  };

  const onPick = async (e) => {
    await uploadFiles(Array.from(e.target.files || []));
    if (ref.current) ref.current.value = "";
  };

  const onDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    const dropped = Array.from(e.dataTransfer?.files || []);
    if (dropped.length) await uploadFiles(dropped);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dragging) setDragging(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  };

  const openPicker = () => ref.current?.click();
  const onKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openPicker(); }
  };

  const remove = (id) => setFiles(files.filter((f) => f.id !== id));

  return (
    <div className="space-y-2">
      <div
        role="button"
        tabIndex={0}
        aria-label="Zona para soltar o seleccionar archivos"
        onClick={openPicker}
        onKeyDown={onKeyDown}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragEnter={onDragOver}
        onDragLeave={onDragLeave}
        className={`flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-4 py-6 text-center cursor-pointer transition-colors ${
          dragging
            ? "border-primary bg-primary/10"
            : "border-border bg-muted/30 hover:bg-muted/50"
        }`}
        data-testid={`${testid}-dropzone`}
      >
        <input
          ref={ref}
          type="file"
          multiple
          accept="image/*,application/pdf,text/plain,text/csv,.doc,.docx,.dcm"
          className="hidden"
          onChange={onPick}
          data-testid={`${testid}-input`}
        />
        {busy ? (
          <Loader2 className="w-6 h-6 text-primary animate-spin" />
        ) : (
          <UploadCloud className={`w-6 h-6 ${dragging ? "text-primary" : "text-muted-foreground"}`} />
        )}
        <div className="text-sm">
          <span className="font-medium">
            {busy ? "Subiendo…" : dragging ? "Suelta los archivos aquí" : "Arrastra archivos o haz clic para seleccionar"}
          </span>
        </div>
        <div className="text-xs text-muted-foreground">
          Imágenes, PDF, DICOM · Múltiples archivos permitidos
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={(e) => { e.stopPropagation(); openPicker(); }}
          disabled={busy}
          data-testid={`${testid}-btn`}
          className="mt-1"
        >
          <Paperclip className="w-4 h-4 mr-1" /> Elegir archivos
        </Button>
      </div>
      {files && files.length > 0 && (
        <div className="space-y-1">
          {files.map((f) => {
            const Ic = ICON(f.content_type);
            return (
              <div key={f.id} className="flex items-center justify-between bg-[#F3F1EC] rounded px-2 py-1 text-xs" data-testid={`attached-${f.id}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <Ic className="w-4 h-4 shrink-0 text-primary" />
                  <span className="truncate">{f.filename}</span>
                </div>
                <button
                  type="button"
                  onClick={() => remove(f.id)}
                  className="text-muted-foreground hover:text-destructive ml-2"
                  aria-label={`Quitar ${f.filename}`}
                  data-testid={`attached-remove-${f.id}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function AttachmentList({ items }) {
  if (!items || items.length === 0) return null;
  const open = (id) => {
    const token = localStorage.getItem("token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/files/${id}/download?auth=${encodeURIComponent(token || "")}`;
    window.open(url, "_blank");
  };
  return (
    <div className="space-y-1 mt-2">
      {items.map((f) => {
        const Ic = ICON(f.content_type);
        return (
          <button
            key={f.id}
            type="button"
            onClick={() => open(f.id)}
            className="flex items-center gap-2 text-xs bg-white/50 hover:bg-white border border-border rounded px-2 py-1 w-full"
            data-testid={`attachment-link-${f.id}`}
          >
            <Ic className="w-4 h-4 text-primary shrink-0" />
            <span className="truncate flex-1 text-left">{f.filename}</span>
            <Download className="w-3 h-3 text-muted-foreground" />
          </button>
        );
      })}
    </div>
  );
}
